import { z } from 'zod';
/**
 * Run custom rules in parallel with the base schema, then return both sets of errors.
 *
 * Why: In Zod 4, `z.preprocess` + `addIssue` skips the inner schema. Use this when
 * both must always run (e.g. conditional messages and "required") — same as Zod 3
 * preprocess behavior.
 *
 * Example:
 *   refineSchema(baseSectionA, (input, ctx) => {
 *     sectionAConsistency({ input, ctx, version, isSection: true });
 *   });
 */
export function refineSchema(schema, rules) {
    const combined = z.unknown().superRefine((input, ctx) => {
        // 1) Custom / conditional rules (may add issues)
        rules(input, ctx);
        // 2) Base schema checks (required, types, nested objects, …)
        const fieldResult = schema.safeParse(input);
        if (!fieldResult.success) {
            for (const issue of fieldResult.error.issues) {
                ctx.addIssue({
                    code: 'custom',
                    message: issue.message,
                    path: issue.path,
                });
            }
        }
    });
    // Callers may read `.shape` / `.isOptional()` (e.g. OASIS UI).
    // Copy those from the base schema onto this wrapper so that keeps working.
    const anySchema = schema;
    Object.assign(combined, {
        shape: anySchema.shape,
        isOptional: () => schema.isOptional(),
    });
    return combined;
}
